import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/apiService";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import {
  Plus,
  Wallet,
  TrendingUp,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Trash2,
  CalendarIcon,
  Filter,
  RefreshCcw,
  X,
  CreditCard,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import Swal from "sweetalert2";

export function ClientBilling() {
  const [billingData, setBillingData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // -- Filter State --
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectSearch, setProjectSearch] = useState("");

  // -- Modal State --
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [method, setMethod] = useState("Cash");
  const [reference, setReference] = useState("");

  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  // SweetAlert2 custom classes
  const swalCustomClasses = {
    container: "swal-container",
    popup: "swal-popup !max-w-md",
    title: "swal-title",
    htmlContainer: "swal-content",
    confirmButton: "swal-confirm-button",
    cancelButton: "swal-cancel-button",
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiService.getBillingSummary();
      if (res.data) {
        setBillingData(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const selectedProjectData = billingData.find((p) => p.id === selectedProject);

  const getFilteredData = () => {
    return billingData.filter((item) => {
      const matchesSearch =
        projectSearch.trim() === "" ||
        String(item.title || "")
          .toLowerCase()
          .includes(projectSearch.toLowerCase().trim()) ||
        String(item.client_name || "")
          .toLowerCase()
          .includes(projectSearch.toLowerCase().trim());

      if (!matchesSearch) return false;

      if (statusFilter === "all") return true;

      const cost = parseFloat(item.total_cost || 0);
      const paid = parseFloat(item.total_paid || 0);
      const balance = parseFloat(item.balance || 0);

      if (statusFilter === "paid") return balance <= 0 && cost > 0;
      if (statusFilter === "unpaid") return paid === 0;
      if (statusFilter === "partial") return paid > 0 && balance > 0;
      return true;
    });
  };

  const filteredData = getFilteredData();

  const toggleRow = (projectId: string) => {
    setExpandedRows((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  const handleAddPayment = async () => {
    // 1. Required fields validation
    if (!selectedProject) {
      await Swal.fire({
        icon: "error",
        title: "Project Required",
        text: "Please select a project to record payment for.",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (!amount || amount.trim() === "") {
      await Swal.fire({
        icon: "error",
        title: "Amount Required",
        text: "Please enter the payment amount.",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (!date) {
      await Swal.fire({
        icon: "error",
        title: "Date Required",
        text: "Please select the payment date.",
        customClass: swalCustomClasses,
      });
      return;
    }

    // 2. Validate amount is a valid positive number
    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      await Swal.fire({
        icon: "error",
        title: "Invalid Amount",
        text: "Payment amount must be a number greater than 0.",
        customClass: swalCustomClasses,
      });
      return;
    }

    // NEW: Enforce max amount limit of 1,000,000
    if (paymentAmount > 1000000) {
      await Swal.fire({
        icon: "error",
        title: "Amount Exceeds Limit",
        text: "The maximum allowed payment amount is ₱1,000,000.",
        customClass: swalCustomClasses,
      });
      return;
    }

    // 3. Get project data and validate existence
    const project = billingData.find((item) => item.id === selectedProject);
    if (!project) {
      await Swal.fire({
        icon: "error",
        title: "Project Not Found",
        text: "Selected project could not be found. Please try again.",
        customClass: swalCustomClasses,
      });
      return;
    }

    // 4. Check against remaining balance
    const totalCost = parseFloat(project.total_cost || "0");
    const totalPaid = parseFloat(project.total_paid || "0");
    const remainingBalance = totalCost - totalPaid;

    // Prevent overpayment (with small epsilon for floating-point precision)
    if (paymentAmount > remainingBalance + 0.01) {
      await Swal.fire({
        icon: "error",
        title: "Overpayment Detected",
        text: `You cannot pay ${formatMoney(paymentAmount)}. The remaining balance is only ${formatMoney(remainingBalance)}.`,
        customClass: swalCustomClasses,
      });
      return;
    }

    // if reference number is > 20 return the sweet alert

    if (reference.trim().length > 20) {
      await Swal.fire({
        icon: "error",
        title: "Reference Too Long",
        text: "Reference number must be 20 characters or less.",
        customClass: swalCustomClasses,
      });
      return;
    }

    // 5. Confirmation dialog with details
    const confirmResult = await Swal.fire({
      title: "Confirm Payment",
      html: `
        <div class="text-left space-y-2 text-sm">
          <p><strong>Project:</strong> ${project.title}</p>
          <p><strong>Client:</strong> ${project.client_name || "N/A"}</p>
          <p><strong>Amount:</strong> ${formatMoney(paymentAmount)}</p>
          <p><strong>Date:</strong> ${format(new Date(date), "MMM dd, yyyy")}</p>
          <p><strong>Method:</strong> ${method}</p>
          ${reference ? `<p><strong>Reference:</strong> ${reference}</p>` : ""}
          <p class="mt-4 font-medium text-green-600">
            Remaining balance after payment: ${formatMoney(remainingBalance - paymentAmount)}
          </p>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Confirm Payment",
      cancelButtonText: "Cancel",
      customClass: {
        ...swalCustomClasses,
        popup: "swal-popup !max-w-lg",
      },
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    // 6. Show loading state
    Swal.fire({
      title: "Processing Payment...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    // Wait exactly 2 seconds
    await new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      await apiService.createPayment({
        projectId: selectedProject,
        amount: paymentAmount,
        date,
        method,
        reference,
      });

      await Swal.fire({
        icon: "success",
        title: "Payment Recorded!",
        text: "The payment has been successfully saved.",
        timer: 2200,
        showConfirmButton: false,
        customClass: swalCustomClasses,
      });

      setIsModalOpen(false);
      setAmount("");
      setReference("");
      fetchData();
    } catch (error: any) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to record payment. Please try again.",
        customClass: swalCustomClasses,
      });
    } finally {
      Swal.close();
    }
  };

  const handleDeletePayment = async (id: number) => {
    const result = await Swal.fire({
      title: "Delete this payment?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#ef4444",
      customClass: swalCustomClasses,
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
          customClass: swalCustomClasses,
        });

        await new Promise((resolve) => setTimeout(resolve, 2000));

        await apiService.deletePayment(id);
        fetchData();
        await Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Payment has been successfully deleted.",
          timer: 1800,
          showConfirmButton: false,
          customClass: swalCustomClasses,
        });
      } catch (e) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "Could not delete payment. Please try again.",
          customClass: swalCustomClasses,
        });
      } finally {
        Swal.close();
      }
    }
  };

  // Totals Calculation
  const totalRevenue = billingData.reduce(
    (sum, item) => sum + parseFloat(item.total_cost || 0),
    0,
  );
  const totalCollected = billingData.reduce(
    (sum, item) => sum + parseFloat(item.total_paid || 0),
    0,
  );
  const totalReceivable = totalRevenue - totalCollected;

  return (
    <div className="flex min-h-screen flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-left">
          <h2 className="text-xl font-bold sm:text-2xl">
            <CreditCard className="mr-2 mb-1 inline-block h-6 w-6 text-muted-foreground" />
            Client Billing
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage invoices, track payments, and receivables
          </p>
        </div>

        <div className="flex w-full flex-col-reverse items-center gap-3 sm:w-auto sm:flex-row">
          <Button
            variant="outline"
            onClick={fetchData}
            className="h-11 w-full shrink-0 rounded-md sm:w-11 sm:px-0"
          >
            <RefreshCcw
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            <span className="sm:hidden ml-2">Refresh</span>
          </Button>

          <Button
            onClick={() => setIsModalOpen(true)}
            className="w-full flex-1 rounded-md sm:w-auto sm:flex-none"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="whitespace-nowrap">Record Payment</span>
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <Card className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground">
          <CardHeader className="flex flex-row items-center justify-between px-5 pb-0 pt-4">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Contract Value
            </CardTitle>
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-muted text-foreground">
              <Wallet className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4 pt-2">
            <div
              className="truncate text-[1.45rem] font-bold leading-none tracking-[-0.03em] text-foreground"
              title={formatMoney(totalRevenue)}
            >
              {formatMoney(totalRevenue)}
            </div>
            <p className="mt-2 text-sm leading-5 text-muted-foreground">
              Across all active projects
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground">
          <CardHeader className="flex flex-row items-center justify-between px-5 pb-0 pt-4">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Collected
            </CardTitle>
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-muted text-foreground">
              <TrendingUp className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4 pt-2">
            <div
              className="truncate text-[1.45rem] font-bold leading-none tracking-[-0.03em] text-foreground"
              title={formatMoney(totalCollected)}
            >
              {formatMoney(totalCollected)}
            </div>
            <p className="mt-2 text-sm leading-5 text-muted-foreground">
              {totalRevenue > 0
                ? ((totalCollected / totalRevenue) * 100).toFixed(1)
                : 0}
              % of total value
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground">
          <CardHeader className="flex flex-row items-center justify-between px-5 pb-0 pt-4">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Outstanding Balance
            </CardTitle>
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-muted text-foreground">
              <AlertCircle className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4 pt-2">
            <div
              className="truncate text-[1.45rem] font-bold leading-none tracking-[-0.03em] text-foreground"
              title={formatMoney(totalReceivable)}
            >
              {formatMoney(totalReceivable)}
            </div>
            <p className="mt-2 text-sm leading-5 text-muted-foreground">
              Pending collection
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground">
        <CardHeader className="border-b border-border px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-foreground">Project Accounts</CardTitle>
              <CardDescription className="mt-1">
                Click on a row to view detailed payment history.
              </CardDescription>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <div className="relative w-full sm:w-[260px]">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="Search Project..."
                  className="h-11 rounded-md pl-11"
                />
              </div>

              <div className="w-full sm:w-[200px]">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-11 rounded-md">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Filter by status" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="partial">Partially Paid</SelectItem>
                    <SelectItem value="paid">Fully Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto p-5">
            <Table>
              <TableHeader className="bg-muted">
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead className="min-w-[220px] px-4 py-4 text-xs font-semibold uppercase tracking-[0.14em]">
                    Project / Client
                  </TableHead>
                  <TableHead className="px-4 py-4 text-xs font-semibold uppercase tracking-[0.14em]">Status</TableHead>
                  <TableHead className="px-4 py-4 text-right whitespace-nowrap text-xs font-semibold uppercase tracking-[0.14em]">
                    Total Cost
                  </TableHead>
                  <TableHead className="px-4 py-4 text-right whitespace-nowrap text-xs font-semibold uppercase tracking-[0.14em]">
                    Paid
                  </TableHead>
                  <TableHead className="px-4 py-4 text-right whitespace-nowrap text-xs font-semibold uppercase tracking-[0.14em]">
                    Balance
                  </TableHead>
                  <TableHead className="w-[180px] min-w-[150px] px-4 py-4 text-xs font-semibold uppercase tracking-[0.14em]">
                    Payment Progress
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-14 text-center text-muted-foreground"
                    >
                      Loading financial data...
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-14 text-center text-muted-foreground"
                    >
                      No projects found matching that filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item) => {
                    const cost = parseFloat(item.total_cost || 0);
                    const paid = parseFloat(item.total_paid || 0);
                    const balance = parseFloat(item.balance || 0);
                    const percent = cost > 0 ? (paid / cost) * 100 : 0;
                    const isFullyPaid = balance <= 0 && cost > 0;
                    const isExpanded = expandedRows.includes(item.id);

                    return (
                      <React.Fragment key={item.id}>
                        <TableRow
                          className={`cursor-pointer border-border ${isExpanded ? "bg-muted/40" : "hover:bg-muted/20"}`}
                          onClick={() => toggleRow(item.id)}
                        >
                          <TableCell className="pl-4">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-semibold text-foreground">
                                {item.title}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                {item.client_name || "Unknown Client"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {isFullyPaid ? (
                              <Badge
                                variant="default"
                                className="font-medium"
                              >
                                Fully Paid
                              </Badge>
                            ) : paid > 0 ? (
                              <Badge
                                variant="secondary"
                                className="font-medium"
                              >
                                Partial
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-muted-foreground"
                              >
                                Unpaid
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium text-foreground">
                            {formatMoney(cost)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium text-foreground">
                            {formatMoney(paid)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium text-muted-foreground">
                            {formatMoney(balance)}
                          </TableCell>
                          <TableCell className="pr-6">
                            <div className="flex items-center gap-3">
                              <Progress
                                value={percent}
                                className="h-2 w-full"
                              />
                              <span className="w-9 text-right text-xs font-medium text-muted-foreground">
                                {Math.min(Math.round(percent), 100)}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* EXPANDED HISTORY ROW */}
                        {isExpanded && (
                          <TableRow className="border-t-0 bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={7} className="p-0">
                              <div className="py-4 px-4 md:pl-14 md:pr-4 overflow-x-auto">
                                <div className="rounded-xl border border-border bg-card">
                                  <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
                                    <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground">
                                      Transaction History
                                    </h4>
                                    <span className="text-xs text-muted-foreground">
                                      ID: {item.id}
                                    </span>
                                  </div>
                                  {item.payment_history &&
                                  item.payment_history.length > 0 ? (
                                    <Table>
                                      <TableHeader className="bg-muted">
                                        <TableRow className="hover:bg-transparent">
                                          <TableHead className="h-10 px-4 text-xs font-semibold uppercase tracking-[0.14em]">
                                            Date
                                          </TableHead>
                                          <TableHead className="h-10 px-4 text-xs font-semibold uppercase tracking-[0.14em]">
                                            Method
                                          </TableHead>
                                          <TableHead className="h-10 px-4 text-xs font-semibold uppercase tracking-[0.14em]">
                                            Reference
                                          </TableHead>
                                          <TableHead className="h-10 px-4 text-right text-xs font-semibold uppercase tracking-[0.14em]">
                                            Amount
                                          </TableHead>
                                          <TableHead className="h-10 w-[50px] px-4 text-right text-xs font-semibold uppercase tracking-[0.14em]"></TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {item.payment_history.map(
                                          (hist: any) => (
                                            <TableRow
                                              key={hist.id}
                                              className="h-11 border-border hover:bg-muted/40"
                                            >
                                              <TableCell className="whitespace-nowrap py-2 text-sm text-foreground">
                                                <div className="flex items-center gap-2">
                                                  <CalendarIcon className="h-3 w-3 text-muted-foreground" />
                                                  {format(
                                                    new Date(hist.payment_date),
                                                    "MMM d, yyyy",
                                                  )}
                                                </div>
                                              </TableCell>
                                              <TableCell className="py-2 text-sm text-foreground">
                                                {hist.method}
                                              </TableCell>
                                              <TableCell className="py-2 text-sm font-mono text-muted-foreground">
                                                {hist.reference || "-"}
                                              </TableCell>
                                              <TableCell className="py-2 text-right text-sm font-medium text-foreground">
                                                {formatMoney(
                                                  parseFloat(hist.amount),
                                                )}
                                              </TableCell>
                                              <TableCell className="py-2 text-right">
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePayment(
                                                      hist.id,
                                                    );
                                                  }}
                                                >
                                                  <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                      <Wallet className="mb-2 h-8 w-8 text-muted-foreground/40" />
                                      <p className="text-sm text-muted-foreground">
                                        No payment records found.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* CUSTOM MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="modal w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-xl animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Record New Payment</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the payment details below.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsModalOpen(false)}
                className="h-9 w-9 rounded-md"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Project Select */}
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">
                  Select Project
                </label>
                <Select
                  value={selectedProject}
                  onValueChange={setSelectedProject}
                >
                  <SelectTrigger className="h-11 w-full rounded-md">
                    <SelectValue placeholder="Search or select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {billingData.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="font-medium">{p.title}</span>
                        <span className="text-muted-foreground ml-2 text-xs">
                          ({p.client_name})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-muted-foreground">
                      ₱
                    </span>
                    <Input
                      type="number"
                      min={0}
                      max={1000000}
                      step="0.01"
                      className="h-11 rounded-md pl-7"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  {selectedProjectData && (
                    <p className="mt-1 text-right text-[11px] text-muted-foreground">
                      Max allowed:{" "}
                      <span className="font-medium text-foreground">
                        {formatMoney(
                          Math.min(
                            parseFloat(selectedProjectData.total_cost) -
                              parseFloat(selectedProjectData.total_paid),
                            1000000,
                          ),
                        )}
                      </span>
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Date Received
                  </label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="h-11 rounded-md"
                  />
                </div>
              </div>

              {/* Method & Reference */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Payment Method
                  </label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger className="h-11 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                      <SelectItem value="Bank Transfer">
                        Bank Transfer
                      </SelectItem>
                      <SelectItem value="GCash">GCash</SelectItem>
                      <SelectItem value="Maya">Maya</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Reference No. (Optional)
                  </label>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. Check #12345"
                    className="h-11 rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-border bg-muted/30 px-6 py-4">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPayment}
              >
                Save Payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
